import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { 
  PageHeader, 
  Tabs, 
  Badge, 
  Button,
  Card,
  CardContent
} from '../../components/ui';
import { JobDetailSheet } from '../../components/jobs/JobDetailSheet';
import { ResumeSwitcher } from '../../components/ResumeSwitcher';
import { useResumeContext } from '../../hooks/useResumeContext';
import { apiFetch } from '../../lib/api-client';
import type { JobRecommendation } from './discover';
import { Target, Clock, ThumbsDown, Filter } from 'lucide-react';

export default function MatchesPage() {
  const { selectedResumeId, selectedResume, loading: resumeLoading } = useResumeContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedJob, setSelectedJob] = useState<any>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    strategy: '',
    comp: '',
    remote: false,
    title: ''
  });
  
  // Live data from API
  const [matches, setMatches] = useState<JobRecommendation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activeTab = searchParams.get('tab') || 'recommended';

  // Load recommendations when resume changes
  useEffect(() => {
    if (selectedResumeId && !resumeLoading) {
      loadMatches();
    }
  }, [selectedResumeId, resumeLoading]);

  const loadMatches = async () => {
    if (!selectedResumeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const url = new URL('/recommendations', window.location.origin);
      url.searchParams.set('resumeId', selectedResumeId);
      url.searchParams.set('page_size', '20');
      
      const res = await apiFetch(url.pathname + url.search);
      
      if (!res.ok) {
        throw new Error(`Failed to load matches: ${res.status}`);
      }
      
      const data = await res.json();
      
      if (data.success) {
        const items = data.items || [];
        setMatches(items.map((item: any) => ({
          ...item,
          id: item.jobId, // For compatibility
          type: item.employment_type || 'Full-time'
        })));
      } else {
        throw new Error(data.error?.message || 'Failed to load matches');
      }
    } catch (e: any) {
      console.error('Failed to load matches:', e);
      setError(e?.message || 'Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab: string) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  const handleOpen = (jobId: string) => {
    const job = matches.find(j => j.id === jobId || j.jobId === jobId);
    if (job) {
      setSelectedJob({
        ...job,
        description: job.description || 'We are looking for a talented software engineer...',
        salaryMin: job.salaryMin || 180000,
        salaryMax: job.salaryMax || 220000,
        employment_type: job.employment_type || 'Full-time'
      });
      setSheetOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'j' && selectedIndex < matches.length - 1) {
      setSelectedIndex(selectedIndex + 1);
    } else if (e.key === 'k' && selectedIndex > 0) {
      setSelectedIndex(selectedIndex - 1);
    } else if (e.key === 'Enter') {
      handleOpen(matches[selectedIndex]?.id || matches[selectedIndex]?.jobId);
    } else if (e.key === 'o') {
      handleOpen(matches[selectedIndex]?.id || matches[selectedIndex]?.jobId);
    }
  };

  const getStrategyColor = (strategy: string) => {
    switch (strategy) {
      case 'HIGH_COMP': return 'bg-info';
      case 'EASY_WIN': return 'bg-positive';
      case 'SMALL_CO': return 'bg-caution';
      case 'REMOTE': return 'bg-primary';
      default: return 'bg-neutral';
    }
  };

  const renderMatchRow = (match: any, index: number) => (
    <div 
      key={match.id} 
      className={`relative ${index === selectedIndex ? 'ring-2 ring-primary/40' : ''}`}
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      {/* Left color strip for strategy */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStrategyColor(match.strategy)} rounded-l-2xl`} />
      
      <div className="ml-4">
        <div className="flex items-center justify-between p-4 border border-border rounded-2xl bg-card hover:border-border-hover hover:shadow-card-hover focus:outline-none focus:ring-2 focus:ring-primary/40 active:bg-muted/50 active:shadow-sm transition-all cursor-pointer"
             onClick={() => handleOpen(match.id)}
             tabIndex={0}
             onKeyDown={(e) => e.key === 'Enter' && handleOpen(match.id)}>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Badge variant="info">{match.strategy}</Badge>
              <h3 className="font-medium">{match.company} {match.title}</h3>
              <span className="text-sm text-muted-foreground">• {match.salary} • {match.location}</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Why: {match.rationale}; confidence {Math.round(match.confidence * 100)}%
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleOpen(match.id); }}>
              Open
            </Button>
            <Button variant="outline" size="sm" onClick={(e) => e.stopPropagation()}>Save</Button>
          </div>
        </div>
      </div>
    </div>
  );

  const tabItems = [
    {
      label: 'Recommended',
      value: 'recommended',
      content: (
        <div className="space-y-4" onKeyDown={handleKeyDown} tabIndex={-1}>
          {/* Filters Bar */}
          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Target className="h-4 w-4" />
              <span>Attribution-first matches with colored strategy strips</span>
            </div>
            <div className="flex-1" />
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setShowFilters(!showFilters)}
              className="gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <Card>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium">Strategy</label>
                    <div className="relative">
                      <select 
                        value={filters.strategy} 
                        onChange={(e) => setFilters({...filters, strategy: e.target.value})}
                        className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-border-hover transition-colors"
                      >
                        <option value="">All Strategies</option>
                        <option value="HIGH_COMP">High Comp</option>
                        <option value="EASY_WIN">Easy Win</option>
                        <option value="SMALL_CO">Small Co</option>
                        <option value="REMOTE">Remote</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Compensation</label>
                    <div className="relative">
                      <select 
                        value={filters.comp} 
                        onChange={(e) => setFilters({...filters, comp: e.target.value})}
                        className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-border-hover transition-colors"
                      >
                        <option value="">Any Range</option>
                        <option value="0-100k">$0-100k</option>
                        <option value="100k-150k">$100k-150k</option>
                        <option value="150k+">$150k+</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Remote</label>
                    <div className="flex items-center pt-2">
                      <input
                        type="checkbox"
                        checked={filters.remote}
                        onChange={(e) => setFilters({...filters, remote: e.target.checked})}
                        className="mr-2 focus:ring-2 focus:ring-primary/40 focus:ring-offset-2"
                      />
                      <span className="text-sm">Remote only</span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Title contains</label>
                    <input
                      type="text"
                      value={filters.title}
                      onChange={(e) => setFilters({...filters, title: e.target.value})}
                      placeholder="e.g. Senior, Staff..."
                      className="w-full p-2 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 hover:border-border-hover transition-colors"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          
          {loading && matches.length === 0 ? (
            <div className="space-y-4">
              {/* Skeleton layout that mimics actual recommendation cards */}
              {[1, 2, 3].map((i) => (
                <div key={i} className="relative">
                  {/* Strategy strip skeleton */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-muted/30 rounded-l-2xl" />
                  <div className="ml-4">
                    <Card className="border border-border hover:border-border-hover hover:shadow-card-hover transition-all animate-pulse">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="h-5 w-16 bg-muted/50 rounded"></div>
                          <div className="h-5 w-32 bg-muted/50 rounded"></div>
                          <div className="h-4 w-24 bg-muted/30 rounded"></div>
                        </div>
                        <div className="h-4 w-full bg-muted/30 rounded"></div>
                        <div className="h-4 w-3/4 bg-muted/30 rounded"></div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <Card>
              <div className="text-center py-12 bg-destructive/10 rounded-2xl">
                <div className="text-destructive mb-2">Error loading matches</div>
                <p className="text-muted-foreground mb-4">{error}</p>
                <Button onClick={loadMatches} variant="outline">
                  Try Again
                </Button>
              </div>
            </Card>
          ) : matches.length === 0 ? (
            <Card className="border border-border">
              <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
                <Target className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No matches yet</h3>
                <p className="text-muted-foreground mb-4">
                  {selectedResumeId ? 'No job matches found for this resume.' : 'Select a resume to see job matches.'}
                </p>
                {!selectedResumeId && (
                  <Button onClick={() => window.location.href = '/resumes'} className="gap-2">
                    Upload Resume
                  </Button>
                )}
              </div>
            </Card>
          ) : (
            matches.map((match, index) => renderMatchRow(match, index))
          )}
        </div>
      )
    },
    {
      label: 'Viewed',
      value: 'viewed',
      content: (
        <Card>
          <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
            <Clock className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No viewed jobs yet</h3>
            <p className="text-muted-foreground mb-4">
              Jobs you've opened will appear here for easy reference.
            </p>
            <Button onClick={() => window.location.href = '/discover'} className="gap-2">
              Explore Jobs
            </Button>
          </div>
        </Card>
      )
    },
    {
      label: 'Rejected',
      value: 'rejected',
      content: (
        <Card>
          <div className="text-center py-12 bg-champagne-pink/10 rounded-2xl">
            <ThumbsDown className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No rejected jobs</h3>
            <p className="text-muted-foreground mb-4">
              Jobs you've marked as not interested will appear here.
            </p>
            <Button onClick={() => window.location.href = '/discover'} className="gap-2">
              Find Better Matches
            </Button>
          </div>
        </Card>
      )
    }
  ];

  return (
    <div className="container py-6">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="Matches"
          subtitle={selectedResume ? `Job matches for ${selectedResume.filename}` : "Attribution-first list with strategy identification"}
        />
        <ResumeSwitcher className="w-64" />
      </div>

      <Tabs 
        items={tabItems} 
        defaultValue="recommended" 
        value={activeTab}
        onChange={handleTabChange}
      />

      <JobDetailSheet
        isOpen={sheetOpen}
        onClose={() => setSheetOpen(false)}
        job={selectedJob}
        onSave={(id) => console.log('Save:', id)}
        onApply={(id) => console.log('Apply:', id)}
        onReject={(id) => console.log('Reject:', id)}
      />
    </div>
  );
}